import { create } from 'zustand'
import { setAutoFreeze } from 'immer'
import { devtools } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import type { Bet } from 'src/types/domain/Bet'
import { AppErrorTypes, RejectionDetails } from 'src/types/domain/AppError'
import { isAuthenticatedPlayer } from 'src/types/domain/Player'
import { emitBalanceInsufficient } from 'src/services/postMessage/emitters'
import { usePlayerStore } from './player'
import { isDevtoolsEnabled } from './utils/isDevtoolsEnabled'

setAutoFreeze(false)

type UIStore = UIState & { actions: UIActions }

interface NoToast {
  show: false
}

interface SuccessToast {
  show: true
  message: string
  type: 'success'
}

interface ErrorToast {
  show: boolean
  message: string
  type: 'error'
}

interface WarningToast {
  show: true
  message: string
  type: 'warning'
}

type ToastState = NoToast | SuccessToast | ErrorToast | WarningToast

export type IdleState = 'active' | 'warning' | 'paused'

interface UIState {
  isAudioSettingsOpen: boolean
  isSidebarOpen: boolean
  isTracksPanelOpen: boolean
  activeTab: string
  hasInitialBalanceAnimationOccurred: boolean
  toast: ToastState
  idleState: IdleState
  hoveredGameId: string | null
  // v17 (crash mobile): the social view REPLACES the stream in the top region.
  // A shared flag BOTH the stream component (reads it → shrink the real <video>
  // to the top-right corner square) and the social panel (reads it → fill the
  // region instead of a modal overlay) subscribe to, so the swap is a pure
  // CSS/positioning toggle with the <video> staying mounted. It STAYS open
  // across round transitions — only tapping the 'Stream' affordance clears it.
  isSocialFocusOpen: boolean
}

type OutOfRangeType = 'belowRange' | 'aboveRange'
type ForceBetValueType = 'decrease' | 'increase' | 'balance'

export interface UIActions {
  audioSettingsOpened: (isAudioSettingsOpen: boolean) => void
  audioSettingsToggled: () => void
  sidebarOpened: (isSidebarOpen: boolean) => void
  sidebarOpenToggled: () => void
  tracksPanelOpened: (isTracksPanelOpen: boolean) => void
  tabActivated: (activeTab: string) => void
  initialBalanceAnimationOccurred: () => void
  betUnaccepted: (bet: Bet) => void
  betRejected: (errorType: string, detail?: string) => void
  nextRaceBetFailed: () => void
  closeButtonClicked: () => void
  storeBalanceCheckFailed: () => void
  betValueOutOfRange: (type: OutOfRangeType) => void
  forcedBetValue: (type: ForceBetValueType) => void
  autoBettingStopped: () => void
  clearToast: () => void
  setIdleState: (state: IdleState) => void
  resetIdleState: () => void
  setHoveredGameId: (gameId: string | null) => void
  socialFocusOpened: (open: boolean) => void
}

export const useUIStore = create<UIStore>()(
  devtools(
    immer((set, get) => ({
      isSidebarOpen: false,
      isTracksPanelOpen: false,
      isAudioSettingsOpen: false,
      activeTab: 'none',
      hasInitialBalanceAnimationOccurred: false,
      toast: {
        show: false
      },
      idleState: 'active',
      hoveredGameId: null,
      isSocialFocusOpen: false,
      actions: {
        audioSettingsOpened: (isAudioSettingsOpen: boolean) => {
          set({ isAudioSettingsOpen }, undefined, 'audioSettingsOpened')
        },
        audioSettingsToggled: () => {
          set(
            { isAudioSettingsOpen: !get().isAudioSettingsOpen },
            undefined,
            'audioSettingsToggled'
          )
        },
        sidebarOpened: (isSidebarOpen: boolean) => {
          set({ isSidebarOpen }, undefined, 'sidebarOpened')
        },
        sidebarOpenToggled: () => {
          set({ isSidebarOpen: !get().isSidebarOpen }, undefined, 'sidebarOpenToggled')
        },
        tracksPanelOpened: (isTracksPanelOpen: boolean) => {
          set({ isTracksPanelOpen }, undefined, 'tracksPanelOpened')
        },
        tabActivated: (activeTab: string) => {
          set({ activeTab }, undefined, 'tabActivated')
        },
        initialBalanceAnimationOccurred: () => {
          set(
            { hasInitialBalanceAnimationOccurred: true },
            undefined,
            'initialBalanceAnimationOccurred'
          )
        },
        storeBalanceCheckFailed: () => {
          set(
            {
              toast: {
                show: true,
                message: 'toast.errors.insufficientBalance', // Could not place bet, insufficient balance
                type: 'error'
              }
            },
            undefined,
            'insufficientBalance'
          )

          // Emit to parent iframe
          const player = usePlayerStore.getState().currentPlayer
          if (isAuthenticatedPlayer(player)) {
            emitBalanceInsufficient({
              currentBalance: player.balance.amount,
              currency: player.balance.currency
            })
          }
        },
        betRejected: (errorType: string, detail?: string) => {
          set(
            (state) => {
              // The hard close (rev 4): a debit the casino confirmed after the
              // cut is no bet, and its stake is being rolled back.
              if (
                errorType === AppErrorTypes.RoundClosed &&
                detail === RejectionDetails.NotInTime
              ) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.notInTime', // Not in time, stake returning
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.NotEnoughMoney) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.insufficientBalance', // Could not place bet, insufficient balance
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.BonusError) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.BonusError', // Could not place bet, insufficient bonus error
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.BetAmountOutOfRange) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.BetAmountOutOfRange', // Could not place bet, bet amount our of range error
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.BetLimitReached) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.BetLimitReached', // Could not place bet, bet limit reached error
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.BetTypeNotAllowed) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.BetTypeNotAllowed', // Could not place bet, bet type not allowed
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.MaxPayoutReached) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.MaxPayoutReached', // Maximum payout reached, please lower the bet amount
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.RoundClosed) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.roundClosed', // Could not place bet, round is closed
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.RoundMismatch) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.roundMismatch', // Round changed before the bet landed — please try again
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.BetAlreadyExists) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.betExists', // Could not place bet, bet already exists
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.RoundOddsUnavailable) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.roundOddsUnavailable', // Odds for this round are not ready yet
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.OptionNotBettable) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.optionNotBettable', // That option cannot be bet on this round
                  type: 'error'
                }

                return
              }

              if (errorType === AppErrorTypes.OddsMismatch) {
                state.toast = {
                  show: true,
                  message: 'toast.errors.oddsMismatch', // The odds changed before the bet landed
                  type: 'error'
                }

                return
              }

              state.toast = {
                show: true,
                message: 'toast.errors.reasonUnknown', // Bet rejected, reason unknown
                type: 'error'
              }
            },
            undefined,
            'betRejected'
          )
        },
        nextRaceBetFailed: () => {
          set(
            {
              toast: {
                show: true,
                message: 'toast.errors.nextRaceBetFailed', // Next race bet could not be placed, please try again
                type: 'error'
              }
            },
            undefined,
            'nextRaceBetFailed'
          )
        },
        betUnaccepted: (_bet: Bet) => {
          set(
            {
              toast: {
                show: true,
                message: 'toast.errors.betUnaccepted', // Bet could not be placed, please try again
                type: 'error'
              }
            },
            undefined,
            'betUnaccepted'
          )
        },
        closeButtonClicked: () => {
          set({ toast: { show: false } }, undefined, 'closeButtonClicked')
        },
        betValueOutOfRange: (type: OutOfRangeType) => {
          const toast: ToastState =
            type === 'aboveRange'
              ? { show: true, message: 'toast.errors.aboveRange', type: 'error' } // 'Amount is greater than the maximum bet amount'
              : { show: true, message: 'toast.errors.belowRange', type: 'error' } // 'Amount is less than the minimum bet amount'

          set({ toast }, undefined, 'betValueOutOfRange')
        },
        forcedBetValue: (type: ForceBetValueType) => {
          let toast: ToastState
          switch (type) {
            case 'increase':
              toast = { show: true, message: 'toast.succes.forcedBetIncrease', type: 'success' } // Amount set to the maximum bet amount
              break
            case 'decrease':
              toast = { show: true, message: 'toast.succes.forcedBetDecrease', type: 'success' } // Amount set to the minimum bet amount
              break
            case 'balance':
              toast = { show: true, message: 'toast.succes.forcedBetBalance', type: 'success' } // Amount set to the available balance
              break
          }

          set({ toast }, undefined, 'forcedBetValue')
        },
        autoBettingStopped: () => {
          set(
            {
              toast: {
                show: true,
                message: 'toast.succes.autoBettingStopped', // Auto betting is turned off due to insufficient balance
                type: 'success'
              }
            },
            undefined,
            'autoBettingStopped'
          )
        },
        clearToast: () => {
          set({ toast: { show: false } }, undefined, 'clearToast')
        },
        setIdleState: (idleState) => {
          set({ idleState }, undefined, 'setIdleState')
        },
        resetIdleState: () => {
          set({ idleState: 'active' }, undefined, 'resetIdleState')
        },
        setHoveredGameId: (gameId: string | null) => {
          set({ hoveredGameId: gameId }, undefined, 'setHoveredGameId')
        },
        // v17: open/close the mobile crash social view. `false` (the 'Stream'
        // affordance) is the ONLY thing that returns to the stream — round
        // transitions never touch this flag, so an open social view persists.
        socialFocusOpened: (open: boolean) => {
          set({ isSocialFocusOpen: open }, undefined, 'socialFocusOpened')
        }
      }
    })),
    { name: 'UIStore', enabled: isDevtoolsEnabled() }
  )
)

export const useUIActions = () => useUIStore((state) => state.actions)

export const useGetIsAudioSettingsOpen = () => useUIStore((state) => state.isAudioSettingsOpen)
export const useGetIsSidebarOpen = () => useUIStore((state) => state.isSidebarOpen)
export const useGetIsTracksPanelOpen = () => useUIStore((state) => state.isTracksPanelOpen)
export const useGetActiveTab = () => useUIStore((state) => state.activeTab)
export const useGetHasInitialBalanceAnimationOccurred = () =>
  useUIStore((state) => state.hasInitialBalanceAnimationOccurred)
export const useGetToast = () => useUIStore((state) => state.toast)
export const useGetIdleState = () => useUIStore((state) => state.idleState)
export const useGetHoveredGameId = () => useUIStore((state) => state.hoveredGameId)
export const useGetIsSocialFocusOpen = () => useUIStore((state) => state.isSocialFocusOpen)
